import { TriangleAlert } from "lucide-react";
import { motion, useScroll } from "motion/react";
import { useRef } from "react";
import { RevealGroup, RevealItem } from "@/components/motion/motion";
import { cn } from "@/lib/utils";

export type DayMoment = {
	time: string;
	actor: string;
	text: string;
	friction: string;
};

/**
 * A working day told hour by hour. The rail fills as the reader scrolls
 * through the day; every moment carries the friction it costs today.
 */
export function DayTimeline({
	moments,
	frictionLabel,
	className,
}: {
	moments: Record<string, DayMoment>;
	frictionLabel: string;
	className?: string;
}) {
	const listRef = useRef<HTMLDivElement>(null);
	const { scrollYProgress } = useScroll({
		target: listRef,
		offset: ["start 70%", "end 55%"],
	});

	return (
		<div ref={listRef} className={cn("relative", className)}>
			<span
				aria-hidden="true"
				className="absolute top-3 bottom-3 left-[4.75rem] w-px bg-border md:left-[7.25rem]"
			/>
			<motion.span
				aria-hidden="true"
				className="absolute top-3 bottom-3 left-[4.75rem] w-px origin-top bg-signal md:left-[7.25rem]"
				style={{ scaleY: scrollYProgress }}
			/>
			<RevealGroup as="ol" className="relative">
				{Object.entries(moments).map(([key, moment]) => (
					<RevealItem
						as="li"
						key={key}
						className="grid grid-cols-[4.75rem_minmax(0,1fr)] gap-x-0 py-5 md:grid-cols-[7.25rem_minmax(0,1fr)_minmax(0,0.85fr)] md:py-7"
					>
						<p className="pt-0.5 pr-4 font-mono text-sm font-medium text-signal-ink tabular-nums md:pr-8 md:text-right">
							{moment.time}
						</p>
						<div className="relative pl-7 md:pl-10">
							<span
								aria-hidden="true"
								className="absolute top-1.5 -left-[5px] size-[11px] rounded-full border-2 border-signal bg-background"
							/>
							<p className="font-mono text-[11px] tracking-[0.07em] text-muted-foreground uppercase">
								{moment.actor}
							</p>
							<p className="mt-1.5 text-lg text-pretty">{moment.text}</p>
						</div>
						<p className="col-start-2 mt-3 ml-7 flex items-start gap-2.5 self-start rounded-xl border border-dashed border-foreground/20 bg-muted/50 px-3.5 py-2.5 text-sm text-pretty text-muted-foreground md:col-start-3 md:mt-0 md:ml-6">
							<TriangleAlert
								aria-hidden="true"
								className="mt-0.5 size-4 shrink-0 text-signal-ink"
							/>
							<span>
								<span className="sr-only">{frictionLabel} : </span>
								{moment.friction}
							</span>
						</p>
					</RevealItem>
				))}
			</RevealGroup>
		</div>
	);
}
